import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import API from '../../api/API';
import { idDaSlug } from './links';
import NonTrovato from './NonTrovato';
import SchedaCarriera from '../../components/statistiche/SchedaCarriera';
import RigaKpi from '../../components/statistiche/RigaKpi';
import TabellaOrdinabile from '../../components/statistiche/TabellaOrdinabile';
import Piazzamento from '../../components/statistiche/Piazzamento';
import { LinkEdizione, LinkGiocatore } from '../../components/statistiche/Link';
// Breadcrumb, caricamento ed errore hanno già le loro regole nel foglio della sezione: qui si
// riusano invece di ridefinirle, così una modifica al breadcrumb non va fatta in due posti.
import './Statistiche.css';
import './Scheda.css';

// Le colonne stanno fuori dal componente perché TabellaOrdinabile tiene lo stato dell'ordinamento
// e un array ricreato a ogni render lo farebbe ripartire invece di aggiornarlo.
const colonneEdizioni = [
  {
    chiave: 'edizione', etichetta: 'Edizione', testo: true,
    valore: (r) => `${r.torneo} ${r.edizione}`,
    rende: (r) => <LinkEdizione id={r.idEdizione}>{`${r.torneo} ${r.edizione}`}</LinkEdizione>,
  },
  {
    chiave: 'posizione', etichetta: 'Pos.', etichettaLunga: 'Piazz.', soloTabella: true,
    valore: (r) => r.posizione ?? 0,
    rende: (r) => <Piazzamento posizione={r.posizione} />,
  },
  { chiave: 'partite', etichetta: 'P', etichettaLunga: 'Partite', valore: (r) => r.partite },
  { chiave: 'vinte', etichetta: 'V', etichettaLunga: 'Vinte', valore: (r) => r.vinte },
  { chiave: 'pareggiate', etichetta: 'N', etichettaLunga: 'Pareggi', valore: (r) => r.pareggiate },
  { chiave: 'perse', etichetta: 'S', etichettaLunga: 'Perse', valore: (r) => r.perse },
  { chiave: 'meteFatte', etichetta: 'MF', etichettaLunga: 'Mete fatte', valore: (r) => r.meteFatte },
  { chiave: 'meteSubite', etichetta: 'MS', etichettaLunga: 'Mete subite', valore: (r) => r.meteSubite },
  { chiave: 'rosa', etichetta: 'Rosa', etichettaLunga: 'In rosa', valore: (r) => r.rosa },
];

const colonneRosa = [
  {
    chiave: 'giocatore', etichetta: 'Giocatore', testo: true,
    valore: (r) => `${r.cognome} ${r.nome}`,
    rende: (r) => <LinkGiocatore id={r.idGiocatore} nome={r.nome} cognome={r.cognome} />,
  },
  { chiave: 'edizioni', etichetta: 'Ed.', etichettaLunga: 'Edizioni', valore: (r) => r.edizioni },
  { chiave: 'partite', etichetta: 'P', etichettaLunga: 'Partite', valore: (r) => r.partite },
  { chiave: 'mete', etichetta: 'M', etichettaLunga: 'Mete', valore: (r) => r.mete },
  { chiave: 'assist', etichetta: 'A', etichettaLunga: 'Assist', valore: (r) => r.assist },
];

// Le colonne testuali non compaiono nella forma a card, quindi i link che vivono nel loro `rende`
// esistono solo su desktop: senza questi due, da telefono il nome della riga sarebbe testo morto
// e la navigazione fra le schede sparirebbe proprio dove serve di più.
const titoloEdizione = (r) => <LinkEdizione id={r.idEdizione}>{`${r.torneo} ${r.edizione}`}</LinkEdizione>;
const titoloGiocatore = (r) => <LinkGiocatore id={r.idGiocatore} nome={r.nome} cognome={r.cognome} />;

const bilancioEdizione = (r) => `${r.partite} partite · ${r.vinte}V ${r.pareggiate}N ${r.perse}P · ${r.meteFatte}–${r.meteSubite}`;
const sottotitoloRosa = (r) => `${r.edizioni} edizion${r.edizioni === 1 ? 'e' : 'i'}`;

/**
 * La storia di una squadra attraverso i tornei. `Squadra`, nel database, è una riga per edizione
 * col nome a testo libero: qui vengono riunite dalla squadra storica a cui sono agganciate, cioè
 * da un id e non più da un nome, ed è quell'id — non il nome — a decidere l'indirizzo della
 * scheda. È proprio questo che chiude il limite di prima: due gruppi scout con squadre omonime
 * non finiscono più per forza nella stessa scheda, e una squadra che cambia nome nel tempo può
 * restare la stessa storia invece di spezzarsi in due. Il nome mostrato è quello della squadra
 * storica, non quello di una singola edizione.
 */
export default function SchedaSquadra() {
  const { slug } = useParams();
  // Solo per la guardia: la chiamata resta sullo slug intero, perché è il server a risolvere
  // l'id in testa — la coda serve solo a rendere leggibile l'indirizzo, non a identificare la
  // squadra. Uno slug senza id in testa non è un indirizzo valido — CarrieraController
  // risponderebbe comunque 404 — e non merita la chiamata.
  const id = idDaSlug(slug);

  const [storia, setStoria] = useState(null);
  const [caricando, setCaricando] = useState(!!id);
  const [errore, setErrore] = useState(null);

  const carica = useCallback(async () => {
    if (!id) return;
    setCaricando(true);
    setErrore(null);
    try {
      const r = await API.carriera.squadra(slug);
      setStoria(r.isSuccess ? r.data : null);
    } catch (e) {
      setErrore(e.message || 'Errore nel caricamento');
    } finally {
      setCaricando(false);
    }
  }, [id, slug]);

  useEffect(() => { carica(); }, [carica]);

  if (!id) return <NonTrovato cosa="Questa squadra" />;
  if (caricando) return <div className="statistiche-loading">Caricamento…</div>;

  if (errore) {
    return (
      <div className="statistiche-error">
        <h3 className="statistiche-error-title">Errore nel caricamento</h3>
        <p className="statistiche-error-text">{errore}</p>
        <button onClick={carica} className="statistiche-retry-button">Riprova</button>
      </div>
    );
  }

  if (!storia) return <NonTrovato cosa="Questa squadra" />;

  const totali = storia.totali ?? {};
  const edizioni = storia.edizioni ?? [];
  const rosa = storia.rosa ?? [];
  const gruppi = storia.gruppi ?? [];

  const contoEdizioni = totali.edizioni ?? edizioni.length;
  const sottotitolo = [
    gruppi.join(' · '),
    `${contoEdizioni} ${contoEdizioni === 1 ? 'edizione' : 'edizioni'}`,
  ].filter(Boolean).join(' · ');

  return (
    <>
      <SchedaCarriera
        titolo={storia.nome}
        codice={storia.codice && (
          <p className="scheda-codice">
            Codice squadra <strong>{storia.codice}</strong>
          </p>
        )}
        sottotitolo={sottotitolo}
        kpi={<RigaKpi voci={[
          { chiave: 'partite', etichetta: 'Part.', etichettaLunga: 'Partite', valore: totali.partite },
          { chiave: 'vinte', etichetta: 'Vinte', valore: totali.vinte },
          { chiave: 'fatte', etichetta: 'Fatte', valore: totali.meteFatte },
          { chiave: 'subite', etichetta: 'Subite', valore: totali.meteSubite },
          { chiave: 'meglio', etichetta: 'Meglio', valore: totali.migliorPiazzamento ? `${totali.migliorPiazzamento}º` : '—' },
        ]} />}
      >
        <section>
          <h2 className="scheda-sezione-titolo">Per edizione</h2>
          <TabellaOrdinabile
            colonne={colonneEdizioni}
            righe={edizioni}
            chiaveRiga={(r) => r.idEdizione}
            ordineIniziale={{ campo: 'edizione', verso: 'desc' }}
            titoloCard={titoloEdizione}
            sottotitoloCard={bilancioEdizione}
            badgeCard={(r) => <Piazzamento posizione={r.posizione} />}
            vuoto="Nessuna edizione disputata"
          />
        </section>

        <section>
          <h2 className="scheda-sezione-titolo">Hanno giocato qui</h2>
          <TabellaOrdinabile
            colonne={colonneRosa}
            righe={rosa}
            chiaveRiga={(r) => r.idGiocatore}
            ordineIniziale={{ campo: 'mete', verso: 'desc' }}
            titoloCard={titoloGiocatore}
            sottotitoloCard={sottotitoloRosa}
            vuoto="Nessun giocatore registrato"
          />
        </section>
      </SchedaCarriera>
    </>
  );
}
